<template>
  <div class="flex flex-col h-full overflow-hidden">
    <!-- Progress Indicator -->
    <report-progress :showProgress="isLoading" />

    <!-- Dashboard Container -->
    <div class="flex-1 overflow-auto">
      <Tabs
        v-if="categories.length > 0"
        :key="`tabs-${categories.length}`"
        :value="activeTab"
        class="flex flex-col flex-1 overflow-hidden"
      >
        <TabList class="bg-white rounded-lg border shadow-md mb-4">
          <Tab
            v-for="(category, categoryIndex) in categories"
            :key="category.id || category.name"
            :value="categoryIndex"
          >
            <div class="flex items-center gap-2">
              <span>{{ category.name }}</span>
              <span
                class="text-xs font-semibold px-2 py-0.5 rounded"
                :class="getCategoryColor(categoryIndex)"
              >
                {{ category.count }}
              </span>
            </div>
          </Tab>
          <Tab
            v-if="notSelectedTotal > 0"
            value="notSelected"
            :disabled="true"
            class="opacity-100"
          >
            <div class="flex items-center gap-2">
              <span>{{ $t("reports.call_reasons_report.not_selected") }}</span>
              <span
                class="text-xs font-semibold px-2 py-0.5 rounded text-gray-600"
              >
                {{ notSelectedTotal }}
              </span>
            </div>
          </Tab>
        </TabList>

        <TabPanels
          class="flex flex-col flex-1 overflow-auto bg-transparent !p-0"
        >
          <TabPanel
            v-for="(category, categoryIndex) in categories"
            :key="category.id || category.name"
            :value="categoryIndex"
            class="bg-white rounded-lg border shadow-md p-4"
          >
            <div class="flex flex-col">

              <!-- Subcategories Grid -->
              <div
                v-if="
                  category.subcategories && category.subcategories.length > 0
                "
                :class="'grid grid-cols-' + category.subcategories.length + ' gap-2 mt-4'"
              >
                <reason-item
                  v-for="(subcategory, subIndex) in category.subcategories"
                  :key="`cat-${categoryIndex}-sub-${
                    subcategory.id || subIndex
                  }`"
                  :item="subcategory"
                  :category-index="categoryIndex"
                  :depth="0"
                  @export="handleExport"
                />
              </div>
              <div v-else class="text-center text-gray-500 py-8">
                {{
                  $t("reports.call_reasons_report.no_data") ||
                  "No data available"
                }}
              </div>
            </div>
          </TabPanel>
        </TabPanels>
      </Tabs>
      <div
        v-else-if="!isLoading"
        class="flex items-center justify-center h-full"
      >
        <div class="text-center text-gray-500">
          {{ $t("reports.call_reasons_report.no_data") || "No data available" }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Tabs, TabList, Tab, TabPanels, TabPanel } from "primevue";
import ReportProgress from "../../components/ReportProgress.vue";
import ReasonItem from "./components/ReasonItem.vue";
import { mapActions } from "vuex";

export default {
  name: "call-reasons-report",
  components: {
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
    ReportProgress,
    ReasonItem,
  },
  data() {
    return {
      activeTab: 0,
    };
  },
  props: {
    selectedFilters: {
      type: Object,
      default: () => ({}),
    },
    reportData: {
      type: Object,
      default: () => ({}),
    },
    totalCount: {
      type: Number,
      default: 0,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    categories() {
      if (!this.reportData || typeof this.reportData !== "object") {
        return [];
      }

      // Extract all categories from reportData (excluding notSelected)
      const categories = [];
      for (const [key, value] of Object.entries(this.reportData)) {
        if (key !== "notSelected" && value && typeof value === "object") {
          categories.push({
            id: value.id,
            name: key,
            count: parseInt(value.count || 0),
            subcategories: value.subcategories || [],
          });
        }
      }

      return categories;
    },
    notSelectedTotal() {
      return parseInt(this.reportData?.notSelected || 0);
    },
  },
  methods: {
    ...mapActions(["GetCallReasonsReport"]),
    getCategoryColor(index) {
      const colors = [
        "text-red-600",
        "text-green-600",
        "text-blue-600",
        "text-purple-600",
        "text-orange-600",
        "text-teal-600",
        "text-pink-600",
        "text-indigo-600",
      ];
      return colors[index % colors.length];
    },
    async handleExport(reasonId) {
      if (!reasonId) {
        return;
      }

      try {
        const payload = {
          from: this.selectedFilters.from,
          to: this.selectedFilters.to,
          reason: reasonId,
          export: 2,
        };

        await this.GetCallReasonsReport(payload);
      } catch (error) {
        console.error("Export failed:", error);
      }
    },
    goToCallReasonTable() {
      // Navigate to call reason table or emit event
      this.$emit("go-to-table");
    },
  },
  watch: {
    categories: {
      handler(newCategories) {
        // When categories first become available, ensure first tab is active
        if (newCategories && newCategories.length > 0) {
          this.activeTab = 0;
        }
      },
      immediate: true,
    },
  },
};
</script>

<style scoped>
.set-border {
  border: 0.5px solid #d7eaf3;
}
</style>
